import { FC, useContext, useMemo } from "react";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ProviderHubViewEnum, ProviderSearchByEnum, ProviderViewByEnum } from "@app/projects/network-assessment/provider-hub/interfaces";
import { MembershipType } from "@app/exports/enumerations";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import {
  showLegacyQuinnButtons
} from "@app/projects/network-assessment/provider-hub/ai-assistant/aiChatPanelAuthorizationHelper";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import MultiselectNestedDropdown from "@app/shared/checkbox-dropdown/MultiselectNestedDropdown";
import { RegionSetStatusType } from "@app/projects/enumerators";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { UserContext } from "@app/user/UserContext";
import Roles from "@app/user/roles";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { ProviderHubAppliedFilters } from "@app/projects/network-assessment/provider-hub/ProviderHubAppliedFilters";
import ProviderHubFilters from "@app/projects/network-assessment/provider-hub/ProviderHubFilters";
import { ProviderProfileReferrerEnum } from "@app/projects/network-assessment/provider-hub/provider-profile/interfaces/providerProfileReferrerEnum";

interface SearchByOption {
  readonly label: string;
  readonly requiredRole?: Roles;
  readonly value: ProviderSearchByEnum;
}

/** The "Search by" options each "View by" selection allows, in display order. Typed as a complete
 * Record so adding a ProviderViewByEnum member is a compile error until its options are declared. */
const searchByOptionsByViewBy: Readonly<Record<ProviderViewByEnum, readonly SearchByOption[]>> = {
  [ProviderViewByEnum.ContractEntity]: [],
  [ProviderViewByEnum.Hospital]: [
    { label: "Name", value: ProviderSearchByEnum.Name },
    { label: "NPI (Type 2)", value: ProviderSearchByEnum.NPI2 },
    { label: "CCN", value: ProviderSearchByEnum.CCN },
  ],
  [ProviderViewByEnum.Organization]: [
    { label: "Name", value: ProviderSearchByEnum.Name },
    { label: "NPI (Type 2)", value: ProviderSearchByEnum.NPI2 },
  ],
  [ProviderViewByEnum.Provider]: [
    { label: "Name", value: ProviderSearchByEnum.Name },
    { label: "NPI", value: ProviderSearchByEnum.NPI },
    { label: "TIN", requiredRole: Roles.TIN_VIEWER, value: ProviderSearchByEnum.TIN },
  ],
};

export const ProviderHubBasicFilters: FC = () => {
  const providerHubContext = useContext(ProviderHubContext);
  const featureFlagContext = useContext(FeatureFlagContext);
  const userContext = useContext(UserContext);
  const projectContext = useContext(CountyProjectContext);

  const handleSearchInputChange = (enteredValue: string) => {
    providerHubContext.setFilter('searchByInput', enteredValue);
  };

  const viewByOption = providerHubContext.filterState.viewByOption ?? ProviderViewByEnum.Provider;
  const isProviderClaimsInsightsView = providerHubContext.view === ProviderHubViewEnum.ProviderClaimsInsights;

  const viewByOptions = useMemo(() => {
    const options = [ProviderViewByEnum.Provider];

    if (projectContext.project.showAffiliationsInsights === true) {
      options.push(ProviderViewByEnum.ContractEntity);

      if (featureFlagContext.enabledFeatureFlags.has(Features.VIEW_ORGANIZATIONS_IN_PROVIDER_AFFILIATION_PROVIDERHUB)) {
        options.push(ProviderViewByEnum.Organization);
      }
      if (featureFlagContext.enabledFeatureFlags.has(Features.HOSPITAL_PROFILE)) {
        options.push(ProviderViewByEnum.Hospital);
      }
    }

    return options;
  }, [projectContext.project.showAffiliationsInsights, featureFlagContext.enabledFeatureFlags]);

  const searchByOptions = useMemo(() => {
    return searchByOptionsByViewBy[viewByOption]
      .filter(option => option.requiredRole === undefined || userContext.roles.includes(option.requiredRole));
  }, [viewByOption, userContext.roles]);

  const handleViewByChange = (newViewBy: string) => {
    providerHubContext.setFilter('viewByOption', newViewBy as ProviderViewByEnum);
    providerHubContext.setReferrer(ProviderProfileReferrerEnum.ProviderHubLandingPage);
    if (newViewBy === ProviderViewByEnum.ContractEntity) {
      providerHubContext.setFilter('searchByOption', ProviderSearchByEnum.ContractEntity);
    } else {
      providerHubContext.setFilter('searchByOption', ProviderSearchByEnum.Name);
    }
    providerHubContext.setFilter('searchByInput', '');
  };

  const isResultsView = providerHubContext.view === ProviderHubViewEnum.Providers
    || providerHubContext.view === ProviderHubViewEnum.ContractEntity
    || providerHubContext.view === ProviderHubViewEnum.Organization
    || providerHubContext.view === ProviderHubViewEnum.Hospital
    || providerHubContext.view === ProviderHubViewEnum.ProviderClaimsInsights;
  const searchButtonText = isResultsView && providerHubContext.filtersChangedSinceLastSearch ? "Update Results" : "Search";

  return (
    <>
      <div className="provider-hub__landing-page-top-actions-container">
        <>
          {!isProviderClaimsInsightsView && (
            <QESSelect
              allowEmpty={false}
              className="item-column"
              testId="provider-hub__view-by-select"
              label="View by"
              onChange={(e) => handleViewByChange(e.target.value)}
              value={viewByOption}
            >
              {viewByOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </QESSelect>
          )}

          {searchByOptions.length > 0 && !isProviderClaimsInsightsView && (
            <QESSelect
              allowEmpty={false}
              className="item-column"
              testId="provider-hub__search-by-select"
              label="Search by"
              onChange={(e) => providerHubContext.setFilter('searchByOption', e.target.value as ProviderSearchByEnum)}
              value={providerHubContext.filterState.searchByOption}
            >
              {searchByOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </QESSelect>
          )}
        </>

        {!isProviderClaimsInsightsView && (
          <div className="item-column search-input-container">
            <QESTextInput
              testId="provider-hub__search-by-input"
              label={viewByOption === ProviderViewByEnum.ContractEntity ? "Name" : ""}
              inputAppend={<QesIcon iconName={QesIcons.SearchSm} description="Search" />}
              onChange={(e) => handleSearchInputChange(e.target.value)}
              value={providerHubContext.filterState.searchByInput}
              />
          </div>
        )}

        {providerHubContext.showMembershipTypeDropdown && (
          <QESSelect
            allowEmpty={false}
            className="item-column"
            testId="provider-hub__membership_type"
            label="Membership Type"
            onChange={(e) => providerHubContext.setFilter('membershipType', e.target.value as MembershipType)}
            value={providerHubContext.filterState.membershipType}>
            <option
              key={0}
              value={MembershipType.Primary}>
              {providerHubContext.membershipAliases?.primary}
            </option>
            <option
              key={1}
              value={MembershipType.Secondary}>
              {providerHubContext.membershipAliases?.secondary}
            </option>
          </QESSelect>
        )}

        <MultiselectNestedDropdown
          containerClassName="item-column"
          customSelectInputText={providerHubContext.serviceAreaSelectionText}
          filters={providerHubContext.serviceAreaStatusFilters}
          items={providerHubContext.serviceAreaOptions}
          label="Service Area"
          matchFilter={providerHubContext.serviceAreaMatchFilter}
          onClose={() => providerHubContext.setActiveServiceAreaStatusFilters(new Set())}
          onSelectedFiltersChange={(keys) =>
            providerHubContext.setActiveServiceAreaStatusFilters(new Set(keys as RegionSetStatusType[]))
          }
          onSelectionChanged={(selectedItems) => providerHubContext.setFilter('regionSetIds', selectedItems.map(Number))}
          selectedFilters={[...providerHubContext.activeServiceAreaStatusFilters]}
          selectedItems={providerHubContext.filterState.regionSetIds.map(String)}
          testId="provider-hub__service-areas-multi-select-dropdown"
        />

        <MultiselectDropdown
          containerClassName="item-column"
          customWidth={true}
          customSelectInputText={providerHubContext.specialtySelectionText}
          items={providerHubContext.specialtyOptions}
          label="Specialty Group"
          onSelectionChanged={(selectedItems) => providerHubContext.setFilter('specialtyGroupCodes', selectedItems)}
          selectedItems={providerHubContext.filterState.specialtyGroupCodes}
          testId="provider-hub__specialties-multi-select-dropdown"
          showTooltipOnOverflow={true}
        />

        <FlyoutLink
          classNames="item-column advanced-filters-button"
          content={{
            body: <ProviderHubFilters />,
            title: 'Advanced Filters',
          }}
          size="40"
          testId='provider-hub__advanced-filters-button'>
          {providerHubContext.showAdvancedFiltersIndicator && <div className="qes-button__notification-pip" />}
          Advanced Filters
        </FlyoutLink>

        {isProviderClaimsInsightsView && (
          // Fill empty space that the search input would normally fill
          <div className="item-column" aria-hidden="true" />
        )}

        <QESButton
          className="item-column action-buttons"
          disabled={!providerHubContext.isSearchEnabled}
          qesType="secondary"
          qesTestId="provider-hub__reset-all-button"
          onClick={providerHubContext.handleResetAll}>
          Reset All
        </QESButton>

        <QESButton
          className="item-column action-buttons"
          disabled={!providerHubContext.isSearchEnabled}
          id="provider-hub__search-button"
          qesType="primary"
          qesTestId="provider-hub__search-button"
          onClick={providerHubContext.handleSearchClick}>
          {searchButtonText}
        </QESButton>

        {showLegacyQuinnButtons(featureFlagContext.enabledFeatureFlags, userContext.roles) && !isProviderClaimsInsightsView && (
          <QESButton
            disabled={!providerHubContext.isSearchEnabled}
            qesType="outline"
            onClick={() => projectContext.setVirtualAssistantContext({ isOpen: true })}
            qesTestId="provider-hub__show-virtual-assistant"
            className="provider-hub-basic-filters__quinn">
            <QesIcon iconName={QesIcons.Quinn} description="Quinn" />
            <div>
              Ask Quinn
            </div>
          </QESButton>
        )}

      </div>

      <ProviderHubAppliedFilters />
    </>
  )
}